import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Stack from '@mui/joy/Stack';
import Grid from '@mui/joy/Grid';
import IconButton from '@mui/joy/IconButton';
import CircularProgress from '@mui/joy/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { Client } from '../../types/client';
import { Product } from '../../types/product';
import { Sale } from '../../types/sale';
import { clientsApi } from '../../api/clients.api';
import { productsApi } from '../../api/products.api';
import { salesApi } from '../../api/sales.api';
import { formatRecordReference } from '../../utils/formatRecordReference';

interface SaleProduct extends Product {
  quantity: number;
  subtotal: number;
}

const SaleForm: React.FC = () => {
  const navigate = useNavigate();
  const { saleReference } = useParams<{ saleReference?: string }>();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<SaleProduct[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [total, setTotal] = useState(0);
  const [productSelectKey, setProductSelectKey] = useState(0);
  const [sale, setSale] = useState<Sale | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [clientsResponse, productsResponse] = await Promise.all([
          clientsApi.getAll(1, 100),
          productsApi.getAll(),
        ]);
        if (clientsResponse && clientsResponse.data) {
          setClients(clientsResponse.data);
        } else {
          console.error('Respuesta de API de clientes incorrecta:', clientsResponse);
          setClients([]);
        }
        setProducts(productsResponse.data);
        if (saleReference) {
          const saleData = await salesApi.getOne(saleReference);
          setSale(saleData);
          const client = clientsResponse.data?.find(
            (c) => c.reference === saleData.clientReference,
          );
          if (client) {
            setSelectedClient(client);
          }
          if (saleData.saleProducts && saleData.saleProducts.length > 0) {
            const saleProductItems: SaleProduct[] = [];
            for (const sp of saleData.saleProducts) {
              const product = productsResponse.data.find(
                (p) => p.reference === sp.productReference,
              );
              if (product) {
                saleProductItems.push({
                  ...product,
                  quantity: sp.quantity,
                  subtotal: sp.price * sp.quantity,
                });
              }
            }
            setSelectedProducts(saleProductItems);
            setTotal(saleData.total);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [saleReference]);

  useEffect(() => {
    setTotal(
      selectedProducts.reduce(
        (sum, product) => sum + product.price * product.quantity,
        0,
      ),
    );
  }, [selectedProducts]);

  const handleClientSearch = (value: string) => {
    setSearchTerm(value);
    setSelectedClient(null);
  };

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client);
    setSearchTerm('');
  };

  const handleAddProduct = (product: Product) => {
    if (!selectedProducts.find((p) => p.reference === product.reference)) {
      const saleProduct: SaleProduct = {
        ...product,
        quantity: 1,
        subtotal: product.price,
      };
      setSelectedProducts([...selectedProducts, saleProduct]);
    }
    setProductSelectKey((k) => k + 1);
  };

  const handleQuantityChange = (productReference: string, quantity: number) => {
    setSelectedProducts(
      selectedProducts.map((product) => {
        if (product.reference === productReference) {
          return {
            ...product,
            quantity,
            subtotal: product.price * quantity,
          };
        }
        return product;
      }),
    );
  };

  const handleRemoveProduct = (productReference: string) => {
    setSelectedProducts(selectedProducts.filter((p) => p.reference !== productReference));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClient || selectedProducts.length === 0) return;
    try {
      setLoading(true);
      const saleData = {
        clientReference: selectedClient.reference,
        products: selectedProducts.map((product) => ({
          reference: product.reference,
          quantity: product.quantity,
          price: product.price,
        })),
        total,
        status: (sale?.status || 'pending') as Sale['status'],
      };
      if (saleReference) {
        await salesApi.update(saleReference, saleData);
      } else {
        await salesApi.create({
          ...saleData,
          status: 'pending',
        });
      }
      navigate('/sales');
    } catch (error) {
      console.error('Error saving sale:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = searchTerm
    ? clients.filter((client) => client.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : [];

  const availableProducts = products.filter(
    (product) =>
      product.quantity > 0 && !selectedProducts.some((p) => p.reference === product.reference),
  );

  if (loading && clients.length === 0 && products.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const heading = saleReference
    ? `Editar venta #${formatRecordReference(saleReference)}`
    : 'Nueva venta';
  const submitLabel = saleReference ? 'Guardar cambios' : 'Crear venta';

  return (
    <Box sx={{ p: 2, maxWidth: 900, mx: 'auto', width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Typography level="h2">{heading}</Typography>
        <Button variant="outlined" color="neutral" onClick={() => navigate('/sales')}>
          Volver
        </Button>
      </Stack>
      <Sheet variant="outlined">
        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
          <Stack spacing={3}>
            <FormControl>
              <FormLabel>Cliente</FormLabel>
              {selectedClient ? (
                <Sheet variant="soft" sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>{selectedClient.name}</Typography>
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="danger"
                      type="button"
                      onClick={() => setSelectedClient(null)}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                </Sheet>
              ) : (
                <Box sx={{ position: 'relative' }}>
                  <Input
                    value={searchTerm}
                    onChange={(e) => handleClientSearch(e.target.value)}
                    placeholder="Buscar cliente..."
                  />
                  {searchTerm ? (
                    <Sheet
                      variant="outlined"
                      sx={{
                        position: 'absolute',
                        zIndex: 10,
                        left: 0,
                        right: 0,
                        mt: 0.5,
                        maxHeight: 240,
                        overflow: 'auto',
                      }}
                    >
                      <Stack spacing={0}>
                        {filteredClients.map((client) => (
                          <Box
                            key={client.reference}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleClientSelect(client)}
                            onKeyDown={(ev) => {
                              if (ev.key === 'Enter' || ev.key === ' ') {
                                handleClientSelect(client);
                              }
                            }}
                            sx={{
                              p: 1.5,
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'background.level1' },
                            }}
                          >
                            <Typography level="body-sm">{client.name}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Sheet>
                  ) : null}
                </Box>
              )}
            </FormControl>
            <FormControl>
              <FormLabel>Agregar producto</FormLabel>
              <Select
                key={productSelectKey}
                placeholder="Seleccionar producto..."
                value={null}
                onChange={(_e, value) => {
                  if (value == null) return;
                  const product = products.find((p) => p.reference === value);
                  if (product) handleAddProduct(product);
                }}
              >
                {availableProducts.map((product) => (
                  <Option key={product.reference} value={product.reference}>
                    {product.name} — Stock: {product.quantity} — ${product.price}
                  </Option>
                ))}
              </Select>
            </FormControl>
            {selectedProducts.length > 0 ? (
              <Grid container spacing={2}>
                {selectedProducts.map((product) => (
                  <Grid key={product.reference} xs={12} sm={6}>
                    <Sheet variant="soft" sx={{ p: 2 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                        <Typography level="title-sm">{product.name}</Typography>
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          type="button"
                          onClick={() => handleRemoveProduct(product.reference)}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Stack>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                            Precio unitario
                          </Typography>
                          <Typography level="body-sm">${product.price.toFixed(2)}</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                            Cantidad
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <IconButton
                              size="sm"
                              variant="outlined"
                              type="button"
                              onClick={() =>
                                handleQuantityChange(
                                  product.reference,
                                  Math.max(1, product.quantity - 1),
                                )
                              }
                            >
                              <RemoveIcon />
                            </IconButton>
                            <Input
                              type="number"
                              slotProps={{ input: { min: 1, max: product.quantity } }}
                              value={product.quantity}
                              onChange={(e) =>
                                handleQuantityChange(
                                  product.reference,
                                  Math.min(
                                    product.quantity,
                                    Math.max(1, parseInt(e.target.value, 10) || 1),
                                  ),
                                )
                              }
                              sx={{ width: 64, textAlign: 'center' }}
                            />
                            <IconButton
                              size="sm"
                              variant="outlined"
                              type="button"
                              onClick={() =>
                                handleQuantityChange(
                                  product.reference,
                                  Math.min(product.quantity, product.quantity + 1),
                                )
                              }
                            >
                              <AddIcon />
                            </IconButton>
                          </Stack>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography level="title-sm">Subtotal</Typography>
                          <Typography level="title-sm">${product.subtotal.toFixed(2)}</Typography>
                        </Stack>
                      </Stack>
                    </Sheet>
                  </Grid>
                ))}
              </Grid>
            ) : null}
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              spacing={2}
              sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}
            >
              <Typography level="title-lg">Total: ${total.toFixed(2)}</Typography>
              <Button
                type="submit"
                color="primary"
                loading={loading}
                disabled={!selectedClient || selectedProducts.length === 0}
              >
                {submitLabel}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Sheet>
    </Box>
  );
};

export default SaleForm;
